import { Link, useLocation } from 'react-router-dom';
import { BaseLayout, Loader, Menu } from '@insio/ui-toolkit';
import { navigation } from '../navigation';
import { AppRouter } from './AppRouter';
import { AuthenticatedLayout } from './AuthenticatedLayout';
import { MasterAccountBoardLayout } from './MasterAccountBoardLayout';
import {
  AccountPendingDeletionGate,
  isConsentPending,
  isMasterContext,
  useAuth
} from '@/features/auth';
import { isPublicRoute } from '@/features/auth/constants/routes';

export function AppLayout() {
  const location = useLocation();
  const { isLoading, isAuthenticated, user } = useAuth();

  const isPublicRoutePath = isPublicRoute(location.pathname);
  // The policies page is public but viewable while authenticated. Render it inside the
  // authenticated shell like any other in-app route so navigating to/from it doesn't
  // invert the tree and remount AppRouter (which caused a flicker + lost layout state).
  const isPoliciesPage = location.pathname === navigation.policies();
  // While consent is pending the policies page must render standalone (no app menu, no consent modal)
  // so reading it from the modal link can't bypass the gate or loop.
  const consentPending = isConsentPending(user);
  const showAuthenticatedShell =
    isAuthenticated &&
    (!isPublicRoutePath || isPoliciesPage) &&
    !(isPoliciesPage && consentPending);

  if (isLoading) {
    return (
      <div className='flex flex-col h-dvh'>
        <BaseLayout orientation='horizontal' className='flex-1 min-h-0 h-full'>
          <BaseLayout.Menu>
            <Menu
              items={[]}
              showLogo={true}
              showProfile={false}
              orientation='horizontal'
              linkComponent={Link}
              logoHref={navigation.dashboard()}
            />
          </BaseLayout.Menu>
          <BaseLayout.Content>
            <div className='flex items-center justify-center h-full animate-fade-in'>
              <Loader />
            </div>
          </BaseLayout.Content>
        </BaseLayout>
      </div>
    );
  }

  // Account scheduled for deletion: block the whole app on the reactivation gate. The unsubscribe
  // page stays reachable: its link acts for the person it names, not for the signed-in account.
  const isUnsubscribePage = location.pathname === navigation.unsubscribe();
  if (isAuthenticated && user?.account?.status === 'PENDING_DELETION' && !isUnsubscribePage) {
    return <AccountPendingDeletionGate />;
  }

  if (!showAuthenticatedShell) {
    // A protected route without a session renders for one frame right after sign-out,
    // before AuthContext's redirect effect runs — its page hooks would throw outside
    // the authenticated shell's providers. Hold the loader until the redirect lands.
    if (!isAuthenticated && !isPublicRoutePath) {
      return (
        <div className='flex items-center justify-center h-dvh animate-fade-in'>
          <Loader />
        </div>
      );
    }
    return <AppRouter />;
  }

  // A session in a master account is the master account board, not a Tappeer tenant: its own chrome and
  // its own route table.
  if (isMasterContext(user)) {
    return (
      <MasterAccountBoardLayout>
        <AppRouter mode='masterAccountBoard' />
      </MasterAccountBoardLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <AppRouter />
    </AuthenticatedLayout>
  );
}
export default AppLayout;
